/**
 * Cart drawer upsell.
 *
 * Loads Shopify's native product recommendations for the newest cart item and
 * renders them inside the drawer. The drawer re-renders its whole inner HTML on
 * every cart change (see CartDrawerItems.getSectionsToRender), which wipes any
 * JS-injected markup - so this element re-loads whenever it is reconnected or
 * its placeholder comes back empty.
 */
class CartUpsell extends HTMLElement {
  connectedCallback() {
    this.load();
  }

  async load() {
    const productId = this.dataset.productId;
    const limit = this.dataset.limit || 6;

    if (!productId) {
      this.hidden = true;
      return;
    }

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = `${root}recommendations/products?section_id=cart-upsell&limit=${limit}&product_id=${productId}`;

    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`recommendations ${res.status}`);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const inner = doc.querySelector('.cart-upsell__inner');
      const track = this.querySelector('.cart-upsell__track');

      if (!inner || !track || inner.querySelectorAll('.cart-upsell__card').length === 0) {
        this.hidden = true;
        this.classList.remove('is-loaded');
        return;
      }

      track.innerHTML = inner.innerHTML;
      this.hidden = false;
      this.classList.add('is-loaded');
      this.bind(track);
    } catch (e) {
      // Recommendations are a nicety - never let a failure break the drawer.
      this.hidden = true;
      this.classList.remove('is-loaded');
    }
  }

  bind(track) {
    // load() runs on every connectedCallback. The <cart-upsell> element usually
    // gets rebuilt from scratch, but if it is ever merely moved in the DOM the
    // same track would be wired twice and one arrow click would scroll two
    // cards. Wire the listeners once per element.
    if (this._bound) return;
    this._bound = true;

    track.addEventListener('change', (e) => {
      const select = e.target.closest('.cart-upsell__variants');
      if (!select) return;
      const card = select.closest('.cart-upsell__card');
      const button = card && card.querySelector('.cart-upsell__add');
      const price = card && card.querySelector('.cart-upsell__price');
      if (button) button.dataset.variantId = select.value;
      const chosen = select.options[select.selectedIndex];
      if (price && chosen && chosen.dataset.price) price.innerHTML = `<span>${chosen.dataset.price}</span>`;
    });

    track.addEventListener('click', (e) => {
      const button = e.target.closest('.cart-upsell__add');
      if (button) this.add(button);
    });

    this.initNav(track);
  }

  /**
   * Carousel arrows.
   *
   * These scroll to a card's MEASURED offset rather than by a computed delta.
   * The delta version (`scrollBy(track.clientWidth)`) was 12px short on every
   * click: a card is `flex: 0 0 100%` of the track, so it is exactly
   * clientWidth wide, but the track also has a 12px `gap`, making the real
   * card-to-card stride clientWidth + 12. That 12px compounded, and
   * `scroll-snap-type: mandatory` does not reliably re-snap after a
   * programmatic SMOOTH scroll (iOS Safari in particular), so the card came to
   * rest part-way across the panel with its neighbour peeking in.
   *
   * Going to an offset also means a stray swipe cannot poison the next click:
   * currentIndex() picks the nearest card to wherever the track actually sits.
   */
  initNav(track) {
    const prev = this.querySelector('[data-upsell-prev]');
    const next = this.querySelector('[data-upsell-next]');
    if (!prev || !next) return;

    const cards = () => [...track.querySelectorAll('.cart-upsell__card')];

    /** Scroll offset that puts card `i` flush with the left edge of the track. */
    const offsetOf = (list, i) => list[i].offsetLeft - list[0].offsetLeft;

    /** Index of the card nearest the current scroll position. */
    const currentIndex = () => {
      const list = cards();
      if (list.length === 0) return 0;
      const here = track.scrollLeft;
      let best = 0;
      let bestGap = Infinity;
      list.forEach((card, i) => {
        const gap = Math.abs(offsetOf(list, i) - here);
        if (gap < bestGap) {
          bestGap = gap;
          best = i;
        }
      });
      return best;
    };

    const motion = () =>
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    // Where a click has already sent us, while that smooth scroll is still in
    // flight. Without it a second click mid-animation would read currentIndex()
    // as the card we are still leaving, and get swallowed - so tapping the
    // arrow three times quickly would only advance one card.
    let pending = null;

    const goTo = (i) => {
      const list = cards();
      if (list.length === 0) return;
      pending = Math.min(Math.max(i, 0), list.length - 1);
      track.scrollTo({ left: offsetOf(list, pending), behavior: motion() });
    };

    /** The index an arrow should step from: the in-flight target, else reality. */
    const baseIndex = () => (pending === null ? currentIndex() : pending);

    // The moment the user puts a finger or cursor on the track they are in
    // charge, so drop whatever the arrows were aiming at.
    track.addEventListener('pointerdown', () => { pending = null; }, { passive: true });

    let queued = false;
    const sync = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        const list = cards();
        if (list.length === 0) return;

        // Arrived where the last click aimed - hand control back to scrollLeft.
        if (pending !== null && Math.abs(track.scrollLeft - offsetOf(list, pending)) <= 1) {
          pending = null;
        }

        const i = baseIndex();
        prev.disabled = i <= 0;
        next.disabled = i >= list.length - 1;
      });
    };

    prev.addEventListener('click', () => goTo(baseIndex() - 1));
    next.addEventListener('click', () => goTo(baseIndex() + 1));
    track.addEventListener('scroll', sync, { passive: true });

    // Rotating a phone changes clientWidth while scrollLeft keeps its old pixel
    // value, which leaves the track mid-card. Re-align to whatever card is
    // nearest once the new width has settled.
    if (typeof ResizeObserver === 'function') {
      let lastWidth = track.clientWidth;
      new ResizeObserver(() => {
        if (track.clientWidth === lastWidth) return;
        lastWidth = track.clientWidth;
        const list = cards();
        if (list.length === 0) return;
        pending = null;
        track.scrollTo({ left: offsetOf(list, currentIndex()), behavior: 'auto' });
        sync();
      }).observe(track);
    }

    sync();
  }

  async add(button) {
    const id = button.dataset.variantId;
    if (!id) return;

    const original = button.textContent;
    button.disabled = true;
    button.textContent = '...';

    try {
      const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({
          items: [{ id: Number(id), quantity: 1 }],
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      });

      const data = await res.json();
      if (data.status) throw new Error(data.description || data.message);

      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.renderContents === 'function') {
        drawer.renderContents(data);
      } else {
        window.location.reload();
      }
    } catch (e) {
      button.disabled = false;
      button.textContent = original;
    }
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}

/**
 * Discount code field.
 * The Storefront cart API cannot apply discount codes, so this uses Shopify's
 * /discount/<code> route, which applies the code and redirects back.
 */
class CartDiscount extends HTMLElement {
  connectedCallback() {
    const toggle = this.querySelector('.cart-discount__toggle');
    const body = this.querySelector('.cart-discount__body');
    const input = this.querySelector('.cart-discount__input');
    const apply = this.querySelector('.cart-discount__apply');
    if (!toggle || !body) return;

    toggle.addEventListener('click', () => {
      const open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!open));
      body.hidden = open;
      if (!open && input) input.focus();
    });

    const error = this.querySelector('.cart-discount__error');
    const showError = (msg) => {
      if (!error) return;
      error.textContent = msg;
      error.hidden = false;
    };
    const clearError = () => { if (error) { error.hidden = true; error.textContent = ''; } };

    if (input) input.addEventListener('input', clearError);

    /**
     * Applies the code without navigating.
     *
     * GET /discount/<code> applies it to the session; fetching it with
     * same-origin credentials has the same effect as the old redirect, minus
     * the page reload. /cart.js then reports the outcome in `discount_codes`
     * as `{ code, applicable }` - which is authoritative, and unlike comparing
     * totals it also treats a valid free-shipping code (worth 0 off the cart)
     * as a success.
     */
    const submit = async () => {
      const code = (input.value || '').trim();
      if (!code) return;
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      clearError();
      const label = apply ? apply.textContent : '';
      if (apply) { apply.disabled = true; apply.textContent = '...'; }
      if (input) input.disabled = true;

      try {
        await fetch(root + 'discount/' + encodeURIComponent(code), { credentials: 'same-origin' });
        const cart = await fetch(root + 'cart.js', { cache: 'no-store' }).then((r) => r.json());

        const entry = (cart.discount_codes || []).find(
          (d) => String(d.code || '').toLowerCase() === code.toLowerCase()
        );
        const applied = entry
          ? entry.applicable === true
          : (cart.cart_level_discount_applications || []).some(
              (a) => String(a.title || '').toLowerCase() === code.toLowerCase()
            );

        if (!applied) {
          showError('\u201C' + code + '\u201D can\u2019t be applied to this order');
          if (input) { input.disabled = false; input.focus(); input.select(); }
          if (apply) { apply.disabled = false; apply.textContent = label; }
          return;
        }

        // Applied - re-render so the Discounts row and totals reflect it.
        if (typeof window.__openCartDrawerWithFreshCart === 'function') {
          await window.__openCartDrawerWithFreshCart();
        } else {
          window.location.reload();
        }
      } catch (e) {
        showError('Could not apply that code. Please try again.');
        if (input) input.disabled = false;
        if (apply) { apply.disabled = false; apply.textContent = label; }
      }
    };

    if (apply) apply.addEventListener('click', submit);
    if (input) {
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          submit();
        }
      });
    }
  }
}

if (!customElements.get('cart-discount')) {
  customElements.define('cart-discount', CartDiscount);
}

/**
 * Cart drawer behaviour fixes.
 *
 * 1. Minus at quantity 1 removes the line. Dawn clamps the stepper at the
 *    variant's `data-min` (1), so the button just goes dead instead.
 * 2. Guards against the drawer rendering its empty state while the cart still
 *    has items. The drawer's HTML comes from the Section Rendering API against
 *    `sections_url`; if that response is stale/cached the empty branch can win
 *    even though /cart.js disagrees. This re-renders from the live cart.
 */
(function () {
  const ROOT = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // ---- 1. minus at 1 removes the line ----
  document.addEventListener(
    'click',
    (event) => {
      const minus = event.target.closest('.cart-drawer .quantity__button[name="minus"]');
      if (!minus) return;

      const wrapper = minus.closest('quantity-input, .quantity');
      const input = wrapper && wrapper.querySelector('.quantity__input');
      if (!input || parseInt(input.value, 10) > 1) return;

      const row = minus.closest('.cart-item');
      const remove = row && row.querySelector('cart-remove-button a, cart-remove-button button');
      if (!remove) return;

      event.preventDefault();
      event.stopPropagation();
      remove.click();
    },
    true // capture, so this runs before the theme's own stepper handler
  );

  // ---- 2. empty-state guard ----
  let recovering = false;

  async function verifyNotEmpty() {
    if (recovering) return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    const showsEmpty = !!drawer.querySelector('.drawer__inner-empty');
    if (!showsEmpty) return;

    let cart;
    try {
      cart = await fetch(`${ROOT()}cart.js`, { cache: 'no-store' }).then((r) => r.json());
    } catch (e) {
      return;
    }
    if (!cart || cart.item_count === 0) return; // genuinely empty - nothing to fix

    recovering = true;
    try {
      const res = await fetch(`${ROOT()}?section_id=cart-drawer&_=${Date.now()}`, { cache: 'no-store' });
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = doc.querySelector('#CartDrawer');
      const current = drawer.querySelector('#CartDrawer');
      if (fresh && current) current.innerHTML = fresh.innerHTML;
    } catch (e) {
      /* leave the drawer as-is rather than making it worse */
    } finally {
      recovering = false;
    }
  }

  function watch() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    new MutationObserver(() => verifyNotEmpty()).observe(drawer, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch);
  } else {
    watch();
  }
})();

/**
 * Empty-cart product suggestions.
 * These cards live outside <cart-upsell>, so CartUpsell's track-scoped handlers
 * never see them. Delegated from the document instead, which also survives the
 * drawer re-rendering its own HTML.
 */
(function () {
  const ROOT = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  document.addEventListener('change', (event) => {
    const select = event.target.closest('.cart-empty-recs .cart-upsell__variants');
    if (!select) return;
    const card = select.closest('.cart-upsell__card');
    const button = card && card.querySelector('.cart-upsell__add');
    const price = card && card.querySelector('.cart-upsell__price');
    if (button) button.dataset.variantId = select.value;
    const chosen = select.options[select.selectedIndex];
    if (price && chosen && chosen.dataset.price) price.innerHTML = '<span>' + chosen.dataset.price + '</span>';
  });

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('.cart-empty-recs .cart-upsell__add');
    if (!button) return;

    const id = button.dataset.variantId;
    if (!id) return;

    const original = button.textContent;
    button.disabled = true;
    button.textContent = '...';

    try {
      const res = await fetch(ROOT() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify({
          items: [{ id: Number(id), quantity: 1 }],
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();
      if (data.status) throw new Error(data.description || data.message);

      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.renderContents === 'function') {
        // the drawer is in its empty layout - renderContents swaps in the full one
        const inner = drawer.querySelector('.drawer__inner');
        if (inner) inner.classList.remove('is-empty');
        drawer.classList.remove('is-empty');
        drawer.renderContents(data);
      } else {
        window.location.reload();
      }
    } catch (e) {
      button.disabled = false;
      button.textContent = original;
    }
  });
})();

/**
 * Teeinblue (product personalizer) integration.
 *
 * Teeinblue adds to cart through its own widget, bypassing the theme's product
 * form entirely - so none of Dawn's add-to-cart code runs and the drawer never
 * opens. It calls Shopify.onItemAdded when it's done; this theme used to hang
 * Monster Cart's refresh off that same hook. We reuse it to re-render and open
 * the drawer instead.
 */
(function () {
  const ROOT = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  window.Shopify = window.Shopify || {};

  let busy = false;

  async function refreshAndOpenDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || busy) return;
    busy = true;

    try {
      const url = ROOT() + '?sections=cart-drawer,cart-icon-bubble&_=' + Date.now();
      const sections = await fetch(url, { cache: 'no-store' }).then((r) => r.json());

      if (typeof drawer.renderContents === 'function') {
        // the drawer may still be in its empty layout
        const inner = drawer.querySelector('.drawer__inner');
        if (inner) inner.classList.remove('is-empty');
        drawer.classList.remove('is-empty');
        drawer.renderContents({ sections }); // renderContents() opens the drawer itself
      } else {
        drawer.open();
      }
    } catch (e) {
      // never leave the shopper with no feedback
      try { drawer.open(); } catch (e2) {}
    } finally {
      setTimeout(() => { busy = false; }, 300);
    }
  }

  // Teeinblue's documented hook
  window.Shopify.onItemAdded = function () { refreshAndOpenDrawer(); };

  // Belt and braces: some app versions emit an event instead
  ['teeinblue:added-to-cart', 'product:added-to-cart', 'cart:added'].forEach((evt) => {
    document.addEventListener(evt, refreshAndOpenDrawer);
  });

  // exposed so the behaviour can be triggered/tested directly
  window.__openCartDrawerWithFreshCart = refreshAndOpenDrawer;
})();

/**
 * Targeted cart updates.
 *
 * Dawn replaces the whole of `.drawer__inner` on every quantity change, so the
 * entire drawer is destroyed and rebuilt: rows flash, and <cart-upsell>
 * reconnects and re-fetches its recommendations. This patches only what
 * actually changed instead.
 *
 * How it hooks in: CartDrawerItems asks getSectionsToRender() which regions to
 * overwrite. We repoint the cart-drawer entry at #CartDrawer-Sync - an empty
 * node - so Dawn's overwrite is a no-op, while still requesting the section so
 * its HTML arrives in the payload. Dawn then publishes cartUpdate with that
 * payload, and we apply it surgically.
 *
 * Guarded: if the pub/sub globals are missing the override never happens and
 * Dawn's original behaviour stands.
 */
(function () {
  if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
  if (typeof CartDrawerItems === 'undefined') return;

  const $ = (root, sel) => root.querySelector(sel);

  function copyInto(liveEl, newEl) {
    if (!liveEl || !newEl) return;
    if (liveEl.innerHTML !== newEl.innerHTML) liveEl.innerHTML = newEl.innerHTML;
  }

  /** Animate the progress bar rather than replacing it, so the fill transitions. */
  function patchProgress(live, next) {
    const liveBar = $(live, '.cart-fs');
    const nextBar = $(next, '.cart-fs');
    if (!liveBar || !nextBar) return;

    liveBar.className = nextBar.className;
    copyInto($(liveBar, '.cart-fs__msg'), $(nextBar, '.cart-fs__msg'));

    const liveFill = $(liveBar, '.cart-fs__fill');
    const nextFill = $(nextBar, '.cart-fs__fill');
    if (liveFill && nextFill) liveFill.style.width = nextFill.style.width;

    const liveBadge = $(liveBar, '.cart-fs__badge');
    const nextBadge = $(nextBar, '.cart-fs__badge');
    if (liveBadge && nextBadge) {
      liveBadge.style.setProperty('--cart-fs-pct', nextBadge.style.getPropertyValue('--cart-fs-pct'));
      copyInto(liveBadge, nextBadge);
    }
  }

  /** Quantity changes keep the same rows, so update them in place. */
  function patchRows(live, next) {
    const liveList = live.querySelector('#CartDrawer-CartItems');
    const nextList = next.querySelector('#CartDrawer-CartItems');
    if (!liveList || !nextList) return;

    const liveRows = [...liveList.querySelectorAll('.cart-item')];
    const nextRows = [...nextList.querySelectorAll('.cart-item')];

    // A line was added or removed - indices shift, so rebuild the list.
    if (liveRows.length !== nextRows.length) {
      liveList.innerHTML = nextList.innerHTML;
      return;
    }

    liveRows.forEach((row, i) => {
      const nextRow = nextRows[i];
      if (!nextRow) return;

      if (row.className !== nextRow.className) row.className = nextRow.className;

      const input = $(row, '.quantity__input');
      const nextInput = $(nextRow, '.quantity__input');
      if (input && nextInput) {
        const v = nextInput.getAttribute('value');
        if (input.value !== v) {
          input.value = v;
          input.setAttribute('value', v);
        }
      }

      copyInto($(row, '.cart-item__totals'), $(nextRow, '.cart-item__totals'));
      copyInto($(row, '.cart-item__props'), $(nextRow, '.cart-item__props'));
    });
  }

  function patchDrawer(state) {
    const html = state && state.sections && state.sections['cart-drawer'];
    const live = document.querySelector('.drawer__inner');
    if (!html || !live) return;

    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('.drawer__inner');
    if (!next) return;

    // Emptying the cart swaps the whole layout - not worth patching piecemeal.
    if (state.item_count === 0) {
      live.innerHTML = next.innerHTML;
      return;
    }

    copyInto($(live, '.drawer__heading'), $(next, '.drawer__heading'));
    patchProgress(live, next);
    patchRows(live, next);
    copyInto($(live, '.drawer__footer'), $(next, '.drawer__footer'));
    // <cart-upsell> is deliberately untouched so it does not re-fetch.
  }

  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (!event || event.source !== 'cart-items') return;
    try { patchDrawer(event.cartData); } catch (e) { /* leave the drawer as-is */ }
  });

  // Only now redirect Dawn's overwrite, so a failure above leaves it intact.
  //
  // One exception: when the cart empties, let Dawn do its normal full replace.
  // Its focus-trap runs `cartDrawerWrapper.querySelector('.drawer__inner-empty')`
  // straight after and assumes the empty layout is already in the DOM - if we
  // have not replaced it that is null, trapFocus throws, and publish() never
  // runs, so neither the patcher nor the reconciler below ever fires.
  //
  // This method is called again inside the apply loop, after Dawn has set
  // `is-empty` on this element, so the class is a reliable signal there.
  CartDrawerItems.prototype.getSectionsToRender = function () {
    const cartIsNowEmpty = this.classList.contains('is-empty');
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: cartIsNowEmpty ? '.drawer__inner' : '#CartDrawer-Sync',
      },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  };
})();

/**
 * Self-healing reconciliation.
 *
 * Patching in place is faster than Dawn's full replace, but it gives up one
 * safety property: a full replace always painted the newest response, so
 * out-of-order or failed responses could not leave the drawer stale. Rapid
 * clicks (or a dropped patch) can now desync the DOM from the real cart.
 *
 * So after activity settles, compare the DOM against /cart.js and, only if they
 * disagree, re-render the drawer once from the server. Costs one small JSON
 * request per burst of interaction.
 */
(function () {
  if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
  const ROOT = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  let timer = null;
  let running = false;

  async function reconcile() {
    if (running) return;
    const inner = document.querySelector('.drawer__inner');
    if (!inner) return;

    let cart;
    try {
      cart = await fetch(ROOT() + 'cart.js', { cache: 'no-store' }).then((r) => r.json());
    } catch (e) {
      return;
    }

    const domRows = inner.querySelectorAll('.cart-item').length;
    const showsEmpty = !!inner.querySelector('.drawer__inner-empty');
    const consistent =
      cart.item_count === 0 ? showsEmpty && domRows === 0 : !showsEmpty && domRows === cart.items.length;

    if (consistent) return;

    running = true;
    try {
      const sections = await fetch(ROOT() + '?sections=cart-drawer&_=' + Date.now(), { cache: 'no-store' }).then((r) =>
        r.json()
      );
      const next = new DOMParser()
        .parseFromString(sections['cart-drawer'], 'text/html')
        .querySelector('.drawer__inner');
      if (next) inner.innerHTML = next.innerHTML;
    } catch (e) {
      /* leave it rather than make it worse */
    } finally {
      running = false;
    }
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(reconcile, 450);
  }

  subscribe(PUB_SUB_EVENTS.cartUpdate, schedule);
  window.__reconcileCartDrawer = reconcile;
})();

/**
 * Optimistic drawer open.
 *
 * Measured on this store: tapping "Add to cart" left 1.4-1.9s of dead time
 * before the drawer appeared, because Dawn waits for /cart/add to resolve and
 * that request asks Shopify to server-render the whole cart-drawer section.
 *
 * Dawn's own spinner covers the wait, but it sits on the product button - the
 * panel the shopper is waiting for does not move at all. This opens the drawer
 * on the submit event instead, with a skeleton standing in for the cart, and
 * lets renderContents replace it when the real HTML lands.
 *
 * Deliberately conservative:
 *  - capture-phase listener, so it runs before ProductForm's own handler and
 *    cannot be cancelled by it;
 *  - if the add fails (sold out, cart error) the drawer closes again, but only
 *    if WE were the one who opened it;
 *  - a 10s watchdog clears the skeleton even if no event ever arrives, so a
 *    failed request can never strand the drawer in a loading state.
 */
(function () {
  let openedByUs = false;
  let watchdog = null;

  const drawer = () => document.querySelector('cart-drawer');

  // A stand-in for the ONE line being added - not for the whole panel. The
  // drawer's own heading, totals and checkout button are already rendered and
  // correct, so covering them with a full-height placeholder just showed the
  // shopper a blank white sheet. Only the incoming item is unknown.
  function ghostRowMarkup() {
    return (
      '<div class="cart-item cart-ghost" aria-hidden="true">' +
      '<div class="cart-ghost__img"></div>' +
      '<div class="cart-ghost__lines"><span></span><span></span></div>' +
      '</div>'
    );
  }

  function clearSkeleton() {
    document.querySelectorAll('.cart-ghost').forEach((n) => n.remove());
  }

  function stopLoading() {
    clearTimeout(watchdog);
    watchdog = null;
    const d = drawer();
    if (d) d.classList.remove('is-adding');
    clearSkeleton();
    openedByUs = false;
  }

  function startLoading() {
    const d = drawer();
    if (!d) return;

    // #CartDrawer-CartItems exists even when the cart is empty, so the ghost
    // has somewhere to go on a first add too; CSS hides the "cart is empty"
    // panel while is-adding is set.
    const list = d.querySelector('#CartDrawer-CartItems');
    if (list && !list.querySelector('.cart-ghost')) {
      list.insertAdjacentHTML('beforeend', ghostRowMarkup());
    }

    d.classList.add('is-adding');
    if (!d.classList.contains('active')) {
      openedByUs = true;
      d.open();
    }

    clearTimeout(watchdog);
    watchdog = setTimeout(stopLoading, 10000);
  }

  /**
   * Personalisation apps own the add-to-cart flow: teeinblue intercepts the
   * submit, renders a preview, uploads it to photo-api/snap-api and only then
   * adds the line. That takes seconds, it draws its own progress UI, and it may
   * never publish Dawn's cartUpdate - so opening our drawer over the top would
   * hide their loader and risk parking a skeleton until the watchdog fires.
   *
   * On those products, leave the flow alone.
   */
  function hasPersonalizer() {
    // NB: the #teeinblue-product-data script is injected on EVERY product page,
    // personalised or not - checking for the element disables this on the whole
    // store. Only the flags inside it distinguish the two:
    //   hoodie            -> isTeeInBlueProduct: false
    //   custom cat hat    -> isTeeInBlueProduct: true
    const c = window.teeinblueCampaign;
    return !!(c && (c.isTeeInBlueProduct || c.isPlatformProduct));
  }

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.closest('product-form')) return;
      if (!(form.getAttribute('action') || '').includes('/cart/add')) return;
      if (hasPersonalizer()) return;
      startLoading();
    },
    true
  );

  /**
   * Belt and braces: whoever re-renders the drawer - Dawn, our patcher, or an
   * app - the skeleton should go the moment real content lands, not when an
   * event we happen to listen for fires.
   */
  const drawerRoot = document.getElementById('CartDrawer');
  if (drawerRoot && typeof MutationObserver === 'function') {
    // The ghost row lives INSIDE #CartDrawer, so inserting it is itself a
    // mutation. Ignore any batch that only touched ghost nodes, or the
    // observer would clear the ghost the instant it was added.
    const isSkeleton = (node) =>
      node.nodeType === 1 && (node.classList.contains('cart-ghost') || node.closest('.cart-ghost'));

    const touchedRealContent = (mutations) =>
      mutations.some((m) =>
        [...m.addedNodes, ...m.removedNodes].some((n) => n.nodeType === 1 && !isSkeleton(n))
      );

    new MutationObserver((mutations) => {
      const d = drawer();
      if (!d || !d.classList.contains('is-adding')) return;
      if (!touchedRealContent(mutations)) return;
      stopLoading();
    }).observe(drawerRoot, { childList: true, subtree: true });
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, stopLoading);
    subscribe(PUB_SUB_EVENTS.cartError, () => {
      const d = drawer();
      if (openedByUs && d && typeof d.close === 'function') d.close();
      stopLoading();
    });
  }
})();
